import React from "react";

import { faCircle, faFileTimes } from "@awesome.me/kit-4c83d50d0d/icons/classic/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import type { Event } from "~/calendar/CalendarPage";
import isEventHasNoAgenda from "~/calendar/utils/isEventHasNoAgenda";
import { Tooltip } from "~/ui";

import { ReactComponent as NoAgendaOutlined } from "./images/no-agenda.svg";
import { ReactComponent as RecordingOutlinedIcon } from "./images/recording-outlined.svg";
import { ReactComponent as RecordingIcon } from "./images/recording.svg";

import styles from "./EventIcons.module.css";

type TileIcon = "GREEN_DOT" | "NO_AGENDA" | "MEETING_RECORDING";

export function getEventIcons(event: Event, showTileIcons: boolean): TileIcon[] {
	if (event.blockType !== "real" || (!event.hasUnreadNoteContent && !showTileIcons)) return [];

	let icons: TileIcon[] = [];
	if (event.hasUnreadNoteContent) {
		icons.push("GREEN_DOT");
	}

	if (showTileIcons) {
		if (isEventHasNoAgenda(event)) {
			icons.push("NO_AGENDA");
		}

		if (event.hasMeetingRecording) {
			icons.push("MEETING_RECORDING");
		}
	}

	return icons;
}

function IconsPopupContent({ icons }: { icons: TileIcon[] }) {
	if (icons.length === 1) {
		switch (icons[0]) {
			case "GREEN_DOT":
				return <div>Unseen changes</div>;

			case "NO_AGENDA":
				return <div>No agenda</div>;

			case "MEETING_RECORDING":
				return <div>Meeting summary</div>;

			default:
				return null;
		}
	}

	const content = icons.map(icon => {
		switch (icon) {
			case "GREEN_DOT":
				return (
					<div key={icon} style={{ paddingLeft: "2px" }}>
						<FontAwesomeIcon icon={faCircle} className={styles.iconGreenDot} fixedWidth />
						<span style={{ marginLeft: "6px" }}>Unseen changes</span>
					</div>
				);

			case "NO_AGENDA":
				return (
					<div key={icon}>
						<FontAwesomeIcon icon={faFileTimes} className={styles.iconExclamationTriangle} fixedWidth />
						<span style={{ marginLeft: "4px" }}>No agenda</span>
					</div>
				);

			case "MEETING_RECORDING":
				return (
					<div key={icon} style={{ paddingLeft: "2px" }}>
						<RecordingIcon />
						<span style={{ marginLeft: "7px" }}>Meeting summary</span>
					</div>
				);

			default:
				return null;
		}
	});

	return <>{content}</>;
}

function TileIcons({ icons }: { icons: TileIcon[] }) {
	const content = icons.map(icon => {
		switch (icon) {
			case "GREEN_DOT":
				return <FontAwesomeIcon key="green-dot-icon" icon={faCircle} className={styles.iconGreenDot} fixedWidth />;

			case "NO_AGENDA":
				return <NoAgendaOutlined key="no-agenda-icon" />;

			case "MEETING_RECORDING":
				return <RecordingOutlinedIcon key="meeting-recording-icon" />;

			default:
				return null;
		}
	});

	return <>{content}</>;
}

export default function EventIcons({ icons }: { icons: TileIcon[] }) {
	if (icons.length === 0) return null;

	return (
		<Tooltip content={<IconsPopupContent icons={icons} />} placement="bottom">
			<span className={styles.wrapper}>
				<TileIcons icons={[...icons].reverse()} /> {/* We want to have a green dot to be the last icon */}
			</span>
		</Tooltip>
	);
}
